// The CandyVoice voice translation: what a participant says is transcribed,
// translated in the language the others listen to and read with their own
// voice (with Voice Trust+, a standard voice otherwise). It replaces the ohaio
// livespeaker in the meetings of the organizations, see
// VoiceTranslationButton.tsx and Meetingrooms.VoiceTranslation in the backend.
// See docs/Integration_Traduction_Vocale_Kizwork_v3.pdf
import { useEffect } from "react";
import { atom, useAtom } from "jotai";
import { Language } from "../components/Room/LanguageSelect";

// The sentences read aloud to record the voice in each language, in
// public/dist/corpus: a voice can only be recorded in a language having one
const CORPUS: Record<string, string> = {
  af: "af_ZA.txt",
  ar: "ar_JO-kareem-medium.txt",
  az: "az_AZ.txt",
  bg: "bg_BG-dimitar-medium.txt",
  ca: "ca_ES-upc_ona-medium.txt",
  cs: "cs_CZ-jirka-medium.txt",
  cy: "cy_GB-bu_tts-medium.txt",
  da: "da_DK-talesyntese-medium.txt",
  de: "de_DE-thorsten-medium.txt",
  en: "en_US-lessac-medium.txt",
  es: "es_ES-davefx-medium.txt",
  et: "et_EE-news-medium.txt",
  eu: "eu_ES-antton-medium.txt",
  fi: "fi_FI-harri-medium.txt",
  fr: "fr_FR-siwis-medium.txt",
  gl: "gl_ES.txt",
  hi: "hi_IN-pratham-medium.txt",
  ht: "ht_HT.txt",
  hu: "hu_HU-anna-medium.txt",
  hy: "hy_AM-gor-medium.txt",
  id: "id_ID-news_tts-medium.txt",
  is: "is_IS-bui-medium.txt",
  it: "it_IT-serena-medium.txt",
  lt: "lt_LT.txt",
  lv: "lv_LV-aivars-medium.txt",
  mk: "mk_MK.txt",
  ml: "ml_IN-arjun-medium.txt",
  mr: "mr_IN-google-medium.txt",
  mt: "mt_MT.txt",
  nl: "nl_BE-nathalie-medium.txt",
  pl: "pl_PL-gosia-medium.txt",
  pt: "pt_BR-faber-medium.txt",
  ru: "ru_RU-denis-medium.txt",
  sk: "sk_SK-lili-medium.txt",
  sq: "sq_AL-edon-medium.txt",
  sv: "sv_SE-nst-medium.txt",
  tl: "tl_PH.txt",
  tr: "tr_TR-dfki-medium.txt",
  uk: "uk_UA-ukrainian_tts-medium.txt",
  ur: "ur_PK-fasih-medium.txt",
  vi: "vi_VN-vais1000-medium.txt",
  zh: "zh_CN-chaowen-medium.txt",
};

// The languages of CandyVoice, see Meetingrooms.VoiceTranslation.languages/0
// in the backend: the ones a participant can speak, the ones they can listen
// to, and among those the ones the translation is read aloud in (it is only
// shown as text in the others). Empty until they are fetched.
export interface CandyVoiceLanguages {
  spoken: Language[];
  target: Language[];
  voice: string[];
}

const NO_LANGUAGES: CandyVoiceLanguages = { spoken: [], target: [], voice: [] };

// For LanguageSelect: the empty labels make getLanguageLabel ask
// Intl.DisplayNames for the name of the language in the UI language, the code
// is shown when it doesn't know it
const toLanguage = (code: string, voice: string[]): Language => ({
  code,
  native: code,
  en: "",
  ja: "",
  fr: "",
  vi: "",
  voiceRestitution: voice.includes(code),
});

let languagesPromise: Promise<CandyVoiceLanguages> | null = null;

const fetchLanguages = () => {
  languagesPromise ??= fetch("/rapi/voice/languages")
    .then((response) => {
      if (!response.ok) throw new Error(`languages: ${response.status}`);
      return response.json();
    })
    .then(
      ({
        spoken,
        target,
        voice,
      }: {
        spoken: string[];
        target: string[];
        voice: string[];
      }) => ({
        spoken: spoken.map((code) => toLanguage(code, voice)),
        target: target.map((code) => toLanguage(code, voice)),
        voice,
      })
    )
    .catch((error) => {
      // asked again by the next component needing them
      languagesPromise = null;
      throw error;
    });
  return languagesPromise;
};

export const candyVoiceLanguagesAtom = atom<CandyVoiceLanguages>(NO_LANGUAGES);

// The languages of CandyVoice, fetched once (when `enabled`)
export const useCandyVoiceLanguages = (enabled = true) => {
  const [languages, setLanguages] = useAtom(candyVoiceLanguagesAtom);
  useEffect(() => {
    if (!enabled) return;
    fetchLanguages().then(setLanguages, (error) =>
      console.error("voice translation: no languages", error)
    );
  }, [enabled, setLanguages]);
  return languages;
};

// The languages a voice can be recorded in
export const enrollmentLanguages = (languages: CandyVoiceLanguages) =>
  languages.spoken.filter((lang) => CORPUS[lang.code]);

// The 25 sentences to read aloud to record the voice in this language
export const fetchCorpus = async (code: string): Promise<string[]> => {
  const corpus = CORPUS[code];
  if (!corpus) return [];
  const response = await fetch(`/dist/corpus/${corpus}`);
  if (!response.ok) throw new Error(`corpus ${corpus}: ${response.status}`);
  const text = await response.text();
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .slice(0, 25);
};

// Every plan has it (see Meetingrooms.Plans in the backend), unless the admin
// turned it off, or uses the ohaio livespeaker instead
export const voiceTranslationEnabledForOrganization = (
  organization?: { voiceTranslation?: boolean | null } | null
) => organization?.voiceTranslation === true;

// A member of an organization whose meetings are translated with the voice of
// the speaker (the Voice Trust+ add-on) can record their voice, from the user
// menu
export const voiceTranslationEnabledForUser = (user?: User | null) =>
  !!user?.organizations?.some((organization) => organization.ownVoice);

// What the plan of the organization gives, see Meetingrooms.Plans in the
// backend
export const hasFeature = (
  organization: { features?: string[] | null } | null | undefined,
  feature: string
) => !!organization?.features?.includes(feature);

export interface Voice {
  complete: boolean;
  language: string | null;
  // the length of the recording, tells a new voice from the previous one
  seconds?: number | null;
}

// The voice of the authenticated user, see MeetingroomsWeb.VoiceController in
// the backend
export const fetchVoice = async (): Promise<Voice> => {
  const response = await fetch("/rapi/voice");
  if (!response.ok) throw new Error(`voice: ${response.status}`);
  return response.json();
};

// The reasons the backend tells apart when recording the voice
export type EnrollError =
  | "synthetic_voice"
  | "unavailable"
  | "too_many_speakers"
  | "too_big"
  | "invalid"
  | "failed"
  // no answer from the server: the connection was cut, by a proxy timing out
  // for instance, CandyVoice may still have recorded the voice
  | "no_answer";

export const enrollVoice = async (
  language: string,
  audio: Blob
): Promise<Voice> => {
  let response;
  try {
    response = await fetch(
      `/rapi/voice?language=${encodeURIComponent(language)}`,
      {
        method: "POST",
        headers: { "Content-Type": audio.type || "audio/wav" },
        body: audio,
      }
    );
  } catch (e) {
    const error: EnrollError = "no_answer";
    throw error;
  }
  if (!response.ok) {
    let error: EnrollError = "failed";
    try {
      error = (await response.json()).error ?? "failed";
    } catch (e) {
      // not the json of the backend: a proxy refusing a too big body, or
      // answering itself because it gave up waiting (502, 504)
      error = response.status === 413 ? "too_big" : "no_answer";
    }
    throw error;
  }
  return response.json();
};

export const deleteVoice = async () => {
  const response = await fetch("/rapi/voice", { method: "DELETE" });
  if (!response.ok) throw new Error(`voice: ${response.status}`);
};
